import { fetchFormSubmissionCount } from "@/api/forms";
import { formListAnswersCount, isNativeForm } from "@/lib/forms/formSource";
import type { FormListItemDto } from "@/types/formList";

const ENRICH_CONCURRENCY = 8;

async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  mapper: (item: T) => Promise<R>,
): Promise<R[]> {
  if (items.length === 0) return [];

  const results = new Array<R>(items.length);
  let nextIndex = 0;

  async function worker(): Promise<void> {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(items[index]!);
    }
  }

  const workers = Array.from({ length: Math.min(limit, items.length) }, () =>
    worker(),
  );
  await Promise.all(workers);
  return results;
}

export function formsNeedAnswersCountEnrichment(
  forms: FormListItemDto[],
): FormListItemDto[] {
  return forms.filter(
    (form) => isNativeForm(form) && formListAnswersCount(form) == null,
  );
}

export async function enrichFormsWithAnswersCounts(
  forms: FormListItemDto[],
): Promise<FormListItemDto[]> {
  const targets = formsNeedAnswersCountEnrichment(forms);
  if (targets.length === 0) return forms;

  const counts = await mapWithConcurrency(
    targets,
    ENRICH_CONCURRENCY,
    async (form) => {
      try {
        const count = await fetchFormSubmissionCount(form.id);
        return { id: form.id, count };
      } catch {
        return { id: form.id, count: null };
      }
    },
  );

  const countById = new Map<number, number>();
  for (const item of counts) {
    if (item.count != null) {
      countById.set(item.id, item.count);
    }
  }

  if (countById.size === 0) return forms;

  return forms.map((form) => {
    const count = countById.get(form.id);
    return count != null ? { ...form, answersCount: count } : form;
  });
}
